'use client'

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import Image from 'next/image'
import { useMenu } from '@/contexts/MenuContext'

interface NavItem {
	href: string
	scroll: string
	label: string
}

export default function Header() {
	const [isOpen, setIsOpen] = useState(false)
	const [activeSection, setActiveSection] = useState<string>('')
	const { setIsMenuOpen } = useMenu()

	const headerOffset = 80
	const navItems: NavItem[] = [
		{ href: '#rsvp', scroll: '#rsvp2', label: 'Réponse' },
		{ href: '#programme', scroll: '#programme2', label: 'Programme' },
		{ href: '#cagnotte', scroll: '#cagnotte2', label: 'Cagnotte' },
		{ href: '#dress-code', scroll: '#dress-code2', label: 'Moodboard' },
		{ href: '#en-attendant-le-jour-j', scroll: '#en-attendant-le-jour-j2', label: 'En attendant le Jour J' },
	]

	useEffect(() => {
		const onScroll = () => {
			const scrollPos = window.scrollY + headerOffset + 1
			let current = ''
			navItems.forEach(item => {
				const el = document.querySelector(item.href) as HTMLElement | null
				if (el && el.offsetTop <= scrollPos) current = item.href
			})
			setActiveSection(current)
		}
		window.addEventListener('scroll', onScroll, { passive: true })
		onScroll()
		return () => window.removeEventListener('scroll', onScroll)
	}, [navItems])

	useEffect(() => {
		document.body.style.overflow = isOpen ? 'hidden' : 'auto'
		setIsMenuOpen(isOpen) // Notifier le contexte
		return () => { document.body.style.overflow = 'auto' }
	}, [isOpen, setIsMenuOpen])

	const scrollToSection = (href: string) => {
		const el = document.querySelector(href)
		if (el) {
			const y = el.getBoundingClientRect().top + window.pageYOffset - 80
			window.scrollTo({ top: y, behavior: 'smooth' })
		}
		setIsOpen(false)
	}

	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' })
		setIsOpen(false)
	}

	return (
		<header className="fixed top-0 left-0 right-0 z-[70] bg-[#f1ece0] text-[#b19872] border-b border-[#B19872]/30 shadow-sm">
			<div className="container mx-auto flex items-center justify-between h-20">
				{/* Logo */}
				<div onClick={scrollToTop} className="flex items-center cursor-pointer">
					<Image
						src="/img/cachet_logo.png"
						alt="Cachet J&B"
						width={64}
						height={64}
						className="object-contain"
						priority
					/>
				</div>
				{/* Menu Desktop */}
				<nav className="hidden md:flex items-center gap-8 text-lg tracking-wide">
					{navItems.map(item => {
						const isActive = activeSection === item.href
						return (
							<button
								key={item.href}
								onClick={() => scrollToSection(item.href)}
								className={`
    transition-all duration-300 ease-in-out
    font-cormorant
    ${isActive
									? 'font-monsieurLaDoulaise text-3xl hover:text-3xl text-[#700c0c]'
									: 'font-ballet text-lg hover:text-xl hover:text-[#700c0c]'
								}
  `}
							>
								{item.label}
							</button>

						)
					})}
				</nav>
				{/* Burger (mobile) */}
				<button
					onClick={() => setIsOpen(true)}
					className="md:hidden p-2 hover:text-[#B19872]"
					aria-label="Ouvrir le menu"
					aria-expanded={isOpen}
				>
					<Menu className="w-6 h-6" />
				</button>
			</div>

			{/* Mobile FULL SCREEN SLIDE-IN MENU */}
			<AnimatePresence>
				{isOpen && (
					<motion.div
						key="mobile-menu"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 z-[100] bg-[#4B2B2B]/95 backdrop-blur"
					>
						{/* Menu content (slide-in) */}
						<motion.div
							initial={{ x: '100%' }}
							animate={{ x: 0 }}
							exit={{ x: '100%' }}
							transition={{ type: 'spring', stiffness: 320, damping: 30 }}
							className="absolute right-0 top-0 w-[84vw] max-w-xs h-full bg-[#4B2B2B] text-[#F5F1EB] z-[110] flex flex-col p-8"
							onClick={e => e.stopPropagation()}
						>
							<button
								onClick={() => setIsOpen(false)}
								className="absolute right-4 top-4 p-2 text-[#B19872] hover:text-[#fff] z-[120]"
								aria-label="Fermer le menu"
							>
								<X className="w-8 h-8" />
							</button>
							<nav className="flex flex-col gap-7 mt-2">
								{navItems.map(item => {
									const isActive = activeSection === item.href
									return (
										<button
											key={item.href}
											onClick={() => scrollToSection(item.href)}
											className={`block w-full text-left text-xl font-cormorant transition-colors duration-300 ${isActive ? 'font-monsieurLaDoulaise text-[#B19872]' : 'font-ballet hover:text-[#B19872]'}`}
										>
											{item.label}
										</button>
									)
								})}
							</nav>
							{/* Image mobile-only en bas du menu */}
							<div className="mt-auto block md:hidden w-full flex justify-center">
								<Image
									src="/img/IMG_4821.jpg"
									width={200}
									height={200}
									alt="Photo les Doudous"
									className="rounded-xl shadow-lg max-w-xs w-full h-auto object-cover mb-3"
									loading="lazy"
									priority={false}
								/>
							</div>
						</motion.div>
						{/* Overlay pour fermer en cliquant à l'extérieur */}
						<div
							onClick={() => setIsOpen(false)}
							className="absolute inset-0 z-10"
						/>
					</motion.div>
				)}
			</AnimatePresence>
		</header>
	)
}
